import type { TokenCredential } from "@azure/identity";
import { authedJsonPagedValues } from "./http.js";
import { LocalCliError } from "./errors.js";

const ARM = "https://management.azure.com";
const ARM_SCOPE = "https://management.azure.com/.default";
const STORAGE_API = "2023-05-01";
const LA_API = "2023-09-01";

interface StorageEntry {
  name?: string;
  properties?: { primaryEndpoints?: { table?: string }; tags?: Record<string, string> };
  tags?: Record<string, string>;
}
interface WorkspaceEntry { name?: string; properties?: { customerId?: string } }

export interface LedgerResources {
  ledgerTableEndpoint: string; // https://<account>.table.core.windows.net
  workspaceId: string; // LA customerId (the data-plane query id)
}

/**
 * Discover the AgentLedger storage account's table endpoint + the Log
 * Analytics workspace customerId in the given resource group. This is the first
 * ledger *consumer*; both are tagged by the deploy (`m8t:role`), so we
 * pick the role-tagged resources, else the lone candidate.
 */
export async function discoverLedgerResources(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
}): Promise<LedgerResources> {
  const { credential, subscriptionId, resourceGroup } = opts;

  // Paginated: ARM may return empty early pages carrying only a continuation
  // token, and a single-page read would report "no ledger storage account" for
  // a resource group that has one. See authedJsonPagedValues.
  const storage = await authedJsonPagedValues<StorageEntry>({
    credential,
    scope: ARM_SCOPE,
    url: `${ARM}/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.Storage/storageAccounts?api-version=${STORAGE_API}`,
  });
  const accounts = storage.filter((a) => a.properties?.primaryEndpoints?.table);
  const ledgerAcct =
    accounts.find((a) => (a.tags?.["m8t:role"] ?? a.properties?.tags?.["m8t:role"]) === "ledger") ??
    (accounts.length === 1 ? accounts[0] : undefined);
  const ledgerTableEndpoint = ledgerAcct?.properties?.primaryEndpoints?.table?.replace(/\/$/, "");
  if (!ledgerTableEndpoint) {
    throw new LocalCliError({
      code: "DREAM_LEDGER_STORAGE_NOT_FOUND",
      message: `No AgentLedger storage account found in resource group ${resourceGroup}.`,
      hint: `Found: ${accounts.map((a) => a.name).join(", ") || "(none)"}. Tag the ledger account 'm8t:role=ledger'.`,
    });
  }

  const ws = await authedJsonPagedValues<WorkspaceEntry>({
    credential,
    scope: ARM_SCOPE,
    url: `${ARM}/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.OperationalInsights/workspaces?api-version=${LA_API}`,
  });
  const workspaceId = (ws.length === 1 ? ws[0] : ws.find((w) => w.properties?.customerId))?.properties?.customerId;
  if (!workspaceId) {
    throw new LocalCliError({
      code: "DREAM_LA_WORKSPACE_NOT_FOUND",
      message: `No Log Analytics workspace found in resource group ${resourceGroup}.`,
      hint: `Found: ${ws.map((w) => w.name).join(", ") || "(none)"}.`,
    });
  }

  return { ledgerTableEndpoint, workspaceId };
}
